import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Box,
  Chip,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { TIPO_LABEL, formatFeriadoLocalidade, formatFeriadoTooltip, tipoChipSx } from './dateUtils';

const MAX_VISIBLE_TAGS = 2;

const DayCell = ({ day, isCurrentMonth, feriadosDoDia, viewMode, showLocalidade }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const isToday = day.isSame(moment(), 'day');
  const isWeekend = day.day() === 0 || day.day() === 6;
  const validos = feriadosDoDia.filter((f) => f.status === 'VALIDO');
  const invalidos = feriadosDoDia.filter((f) => f.status === 'INVALIDO');
  const visibleValidos = validos.slice(0, MAX_VISIBLE_TAGS);
  const extraCount = validos.length - visibleValidos.length;
  const hasPopoverContent = feriadosDoDia.length > 0;

  const open = Boolean(anchorEl);

  return (
    <Box
      onClick={(e) => hasPopoverContent && setAnchorEl(e.currentTarget)}
      sx={{
        position: 'relative',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1,
        minHeight: viewMode === 'week' ? 120 : 76,
        bgcolor: isWeekend ? 'action.hover' : 'background.paper',
        opacity: isCurrentMonth ? 1 : 0.4,
        cursor: hasPopoverContent ? 'pointer' : 'default',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Box
        sx={{
          alignSelf: 'flex-start',
          ...(isToday
            ? {
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }
            : { px: 0.25, py: 0.125 }),
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: isToday ? 700 : 600,
            fontSize: '0.8125rem',
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            color: isToday ? 'inherit' : 'text.primary',
          }}
        >
          {day.format('D')}
        </Typography>
      </Box>

      <Stack spacing={0.25}>
        {visibleValidos.map((f) => (
          <Tooltip key={f.id} title={formatFeriadoTooltip(f, { showLocalidade })} enterDelay={400}>
            <Chip
              size="small"
              label={f.nome}
              sx={{
                height: 18,
                fontSize: '0.62rem',
                justifyContent: 'flex-start',
                ...tipoChipSx(f.tipo),
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  px: 0.75,
                },
              }}
            />
          </Tooltip>
        ))}
        {extraCount > 0 && (
          <Chip
            size="small"
            variant="outlined"
            label={`+${extraCount}`}
            sx={{ height: 18, fontSize: '0.62rem', alignSelf: 'flex-start' }}
          />
        )}
      </Stack>

      {invalidos.length > 0 && (
        <Tooltip
          title={`${formatFeriadoTooltip(invalidos[0], { showLocalidade })} — feriado cancelado, não conta mais como dia não útil`}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 8,
              height: 8,
              borderRadius: '50%',
              border: '1px dashed',
              borderColor: 'error.main',
            }}
          />
        </Tooltip>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={(e) => {
          e?.stopPropagation?.();
          setAnchorEl(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, maxWidth: 320 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {day.format('DD/MM/YYYY')}
          </Typography>
          <Stack spacing={1}>
            {feriadosDoDia.map((f) => {
              const localidade = showLocalidade ? formatFeriadoLocalidade(f) : null;
              return (
                <Stack key={f.id} spacing={0.25}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip
                      size="small"
                      sx={f.status === 'INVALIDO' ? {} : tipoChipSx(f.tipo)}
                      label={TIPO_LABEL[f.tipo] || f.tipo}
                    />
                    <Typography
                      variant="body2"
                      sx={{ textDecoration: f.status === 'INVALIDO' ? 'line-through' : 'none' }}
                    >
                      {f.nome}
                    </Typography>
                  </Stack>
                  {localidade && (
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                      {localidade}
                    </Typography>
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

DayCell.propTypes = {
  day: PropTypes.object.isRequired,
  isCurrentMonth: PropTypes.bool,
  feriadosDoDia: PropTypes.array,
  viewMode: PropTypes.oneOf(['month', 'week']),
  showLocalidade: PropTypes.bool,
};

DayCell.defaultProps = {
  isCurrentMonth: true,
  feriadosDoDia: [],
  viewMode: 'month',
  showLocalidade: false,
};

const WEEK_DAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const CalendarGrid = ({ days, referenceDate, feriadosByDate, viewMode, showLocalidade }) => (
  <Box>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 0.75,
        mb: 1,
      }}
    >
      {WEEK_DAY_LABELS.map((label) => (
        <Typography
          key={label}
          variant="caption"
          align="center"
          sx={{ fontWeight: 600, color: 'text.secondary' }}
        >
          {label}
        </Typography>
      ))}
    </Box>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 0.75,
      }}
    >
      {days.map((day) => (
        <DayCell
          key={day.format('YYYY-MM-DD')}
          day={day}
          isCurrentMonth={viewMode === 'week' || day.isSame(referenceDate, 'month')}
          feriadosDoDia={feriadosByDate.get(day.format('YYYY-MM-DD')) || []}
          viewMode={viewMode}
          showLocalidade={showLocalidade}
        />
      ))}
    </Box>
  </Box>
);

CalendarGrid.propTypes = {
  days: PropTypes.array.isRequired,
  referenceDate: PropTypes.object.isRequired,
  feriadosByDate: PropTypes.instanceOf(Map).isRequired,
  viewMode: PropTypes.oneOf(['month', 'week']).isRequired,
  showLocalidade: PropTypes.bool,
};

CalendarGrid.defaultProps = {
  showLocalidade: false,
};

export default CalendarGrid;
